import React, { useEffect, useState, Fragment } from 'react';
import { CategoryConfig } from '@/shared/types/category';
import { PackagingTab, SourcingHomeTab, SourcingMarketplaceBannerValueProp } from '@/shared/types/sourcing-marketplace';
import { useConfigProvider } from '@/lib/context';
import CategoryCard from '../CategoryCard';
import { STATIC_FILE_URL } from '@/shared/constants';
import { BANNER_VALUE_PROPS, BRAND_LOGOS, HOMEPAGE_TAB_TO_TEXT } from '@/shared/constants/sourcing-marketplace';
import ExplorePietraServiceCard from '../../Common/ExplorePietraServiceCard';
import { getCreatorHubSourcingHomePath } from '@/shared/utils/sourcing-marketplace-util';
import { ParamKey } from '@/shared/types/url-params';
import cN from 'classnames';
import {
  SOURCING_HOME_FAST_SHIPPING_PACKAGING,
  SOURCING_HOME_LOW_COST_PACKAGING,
} from '@/shared/constants/featured-packaging-constants';
import PackagingCardsList from '../PackagingCardsList';
import { LogisticsFeeRate } from '@/shared/types/store';
import { useQueryParams } from '@/shared/hooks';
import { useIntercom } from 'react-use-intercom';
import Image from 'next/image';
import './index.less';

interface IProps {
  tabType: SourcingHomeTab;
  selectedTab: SourcingHomeTab;
  categoryConfig: { [key: string]: CategoryConfig[] };
  logisticsFeeRateLadder: LogisticsFeeRate[];
}

const CategoryStartingPoints = ({ tabType, selectedTab, categoryConfig, logisticsFeeRateLadder }: IProps) => {
  const { isMobile } = useConfigProvider();
  const { queryParams, setQueryParams } = useQueryParams<{}>();
  const { show } = useIntercom();

  const [selectedPackagingTab, setSelectedPackagingTab] = useState<PackagingTab>(PackagingTab.GET_IN_8_10_DAYS);

  const isPackaging = tabType === SourcingHomeTab.PACKAGING;
  const isAllTabSelected = selectedTab === SourcingHomeTab.ALL;
  const showCategoryGrid = !isPackaging || isAllTabSelected || selectedPackagingTab === PackagingTab.BY_CATEGORY;
  const showFastShippingCards = isPackaging && selectedPackagingTab === PackagingTab.GET_IN_8_10_DAYS;
  const showLowCostShippingCards = isPackaging && selectedPackagingTab === PackagingTab.LOW_COST_OPTIONS;

  useEffect(() => {
    const tab = queryParams.get(ParamKey.PACKAGING_TAB);
    if (tab) {
      setSelectedPackagingTab(tab as PackagingTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const togglePackagingTab = (tab: PackagingTab) => {
    setSelectedPackagingTab(tab);
    setQueryParams({ [ParamKey.PACKAGING_TAB]: tab });
  };

  const showIntercom = () => {
    show();
  };

  const getCategoryItems = () => {
    switch (tabType) {
      case SourcingHomeTab.PRODUCTS:
        return categoryConfig[SourcingHomeTab.PRODUCTS];
      case SourcingHomeTab.PACKAGING:
        return categoryConfig[SourcingHomeTab.PACKAGING];
      case SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES:
        return categoryConfig[SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES];
      case SourcingHomeTab.WEBSITE_DEVELOPMENT:
        return categoryConfig[SourcingHomeTab.WEBSITE_DEVELOPMENT];
      default:
        return [];
    }
  };

  const getCardHeader = (item: CategoryConfig) => {
    switch (tabType) {
      case SourcingHomeTab.PRODUCTS:
        return {
          preheader: 'Work With',
          title: `${item.title} Factories`,
          factoryCount: item?.factoryCount,
        };
      case SourcingHomeTab.PACKAGING:
        return {
          preheader: isMobile ? 'Create' : 'Custom Packaging',
          title: item.title,
          factoryCount: item?.factoryCount,
        };
      case SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES:
      case SourcingHomeTab.WEBSITE_DEVELOPMENT:
      default:
        return {
          preheader: 'Hire A',
          title: item.title,
          factoryCount: item?.factoryCount,
        };
    }
  };

  const renderCategoryCard = (category: CategoryConfig, index: number) => {
    const header = getCardHeader(category);
    return (
      <CategoryCard
        showCardFooter={tabType === SourcingHomeTab.PRODUCTS}
        tabType={tabType}
        cardInfo={{
          preheader: header.preheader,
          header: header.title,
          factoryCount: header?.factoryCount,
          logos: category?.brandLogos || [],
        }}
        category={category}
        index={index}
      />
    );
  };

  const renderMessageSpecialistBanner = (
    <div className="message-specialist-banner">
      <div className="left">
        <div className="top">
          <Image
            src={`${STATIC_FILE_URL}/eddc893163ee3fb423debeb61768b768.png`}
            width={50}
            height={50}
            alt="specialist"
          />
          <div className="text">
            <span className="title">Need help placing an order?</span>
            <span className="subtitle">Send designs to a specialist who will help you place an order.</span>
          </div>
        </div>
        <span className="button" onClick={showIntercom}>
          Message specialist
        </span>
      </div>
      <div className="images">
        <Image
          src={`${STATIC_FILE_URL}/0505a82ee678474fd71d814991b50bea.png`}
          width={100}
          height={100}
          alt="custom packaging"
        />
        <Image
          src={`${STATIC_FILE_URL}/d6d9c9ea53a73c8360065c06c390174f.png`}
          width={100}
          height={100}
          alt="custom packaging"
        />
        <Image
          src={`${STATIC_FILE_URL}/0719e4c923ed7e4c9e27daa9e67336fb.png`}
          width={100}
          height={100}
          alt="custom packaging"
        />
        <Image
          src={`${STATIC_FILE_URL}/97b53163b8215e70ec48f680f83a7e40.png`}
          width={100}
          height={100}
          alt="custom packaging"
        />
      </div>
    </div>
  );

  const renderFastShippingCards = (
    <div className="featured-packaging-container">
      <PackagingCardsList
        data={SOURCING_HOME_FAST_SHIPPING_PACKAGING}
        logisticsFeeRateLadder={logisticsFeeRateLadder}
      />
    </div>
  );

  const renderLowCostShippingCards = (
    <div className="featured-packaging-container">
      <PackagingCardsList data={SOURCING_HOME_LOW_COST_PACKAGING} logisticsFeeRateLadder={logisticsFeeRateLadder} />
    </div>
  );

  const renderExplorePietraServicesCard = (valueProp: SourcingMarketplaceBannerValueProp) => {
    return (
      <ExplorePietraServiceCard
        card={valueProp}
        signupRedirect={getCreatorHubSourcingHomePath()}
        utmMedium="category_starting_points"
      />
    );
  };

  return (
    <div className="category-starting-points" key={tabType}>
      {selectedTab === SourcingHomeTab.PACKAGING && (
        <div className="packaging-pills">
          {[PackagingTab.GET_IN_8_10_DAYS, PackagingTab.LOW_COST_OPTIONS, PackagingTab.BY_CATEGORY].map(
            (tabType: PackagingTab, i: number) => (
              <span
                className={cN('pill', {
                  selected: tabType === selectedPackagingTab,
                })}
                key={i}
                onClick={() => togglePackagingTab(tabType)}
              >
                {tabType}
              </span>
            ),
          )}
        </div>
      )}

      {!isAllTabSelected && (
        <>
          {isPackaging && renderMessageSpecialistBanner}
          {showFastShippingCards && renderFastShippingCards}
          {showLowCostShippingCards && renderLowCostShippingCards}
        </>
      )}

      {showCategoryGrid && (
        <div className="inner">
          {isAllTabSelected && <h2 className="title">{HOMEPAGE_TAB_TO_TEXT[tabType]}</h2>}
          <div className="grid">
            {getCategoryItems().map((gridItem, index: number) => (
              <Fragment key={gridItem.key}>
                {renderCategoryCard(gridItem, index)}
                {index === 7 && (
                  <>
                    {renderExplorePietraServicesCard(BANNER_VALUE_PROPS[0])}
                    {renderExplorePietraServicesCard(BANNER_VALUE_PROPS[1])}
                  </>
                )}
                {index === 11 && (
                  <>
                    {renderExplorePietraServicesCard(BANNER_VALUE_PROPS[2])}
                    {renderExplorePietraServicesCard(BANNER_VALUE_PROPS[3])}
                  </>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryStartingPoints;
